// src/pages/Home.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Değişiklikler (audit sonrası):
//   • MODULES: /music→/bonebeat, /form→/boneframe, /games→/boneforge
//   • Modül adları: BoneBeat · BoneFrame · BoneForge
//   • Hero CTA: /bonebeat ve /boneframe rotaları
//   • i18n: useTranslation eklendi, tüm statik metinler t() üzerinden
//   • Hızlı Erişim: MODULES bölümünün altına QuickAccess bileşeni eklendi
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate }            from "react-router-dom";
import { useTranslation }                      from "react-i18next";
import logo                                    from "../assets/logo.png";
import { useAuth, isDisplayNameAvailable }     from "../context/AuthContext";
import { useAdmin }                            from "../context/AdminContext";
import { useSidebar }                          from "../context/SidebarContext";
import Footer                                  from "../components/Footer/Footer";
import { MODERATOR_UIDS }                      from "../constants/roles";

// ═══════════════════════════════════════════════════════════════════════════════
// MODÜL TANIMI — tüm rotalar güncel
// ═══════════════════════════════════════════════════════════════════════════════
const MODULES = [
  {
    id: "bonebeat",
    titleKey: "home.modules.beat.title",       title: "BoneBeat",
    subtitleKey: "home.modules.beat.subtitle",  subtitle: "Müzik Arşivi",
    descKey: "home.modules.beat.desc",
    desc: "Müziğini bir koleksiyoncu titizliğiyle derle. Her liste, tozlu bir rafta bekleyen nadir bir kayıt gibi.",
    href: "/bonebeat",
    iconPath: "M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z",
  },
  {
    id: "boneframe",
    titleKey: "home.modules.frame.title",       title: "BoneFrame",
    subtitleKey: "home.modules.frame.subtitle",  subtitle: "Cinema Library",
    descKey: "home.modules.frame.desc",
    desc: "Gördüğün filmleri bir sinema galerisine dönüştür. Paylaş, keşfet, izleme listeni oluştur.",
    href: "/boneframe",
    iconPath: "M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z",
  },
  {
    id: "boneforge",
    titleKey: "home.modules.forge.title",       title: "BoneForge",
    subtitleKey: "home.modules.forge.subtitle",  subtitle: "The Forge · Smithy",
    descKey: "home.modules.forge.desc",
    desc: "Oyununu, kodunu, scriptini topluluğa sun. Teknik bir ocaktan çıkan her proje burada belgelenir.",
    href: "/boneforge",
    iconPath: "M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z",
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// HATA ÇEVİRİLERİ
// ═══════════════════════════════════════════════════════════════════════════════
// ── Firebase hata kodu → i18n anahtarı eşlemesi ──────────────────────────────
// Hardcode metin YOK — her şey i18n üzerinden gelir.
const FIREBASE_ERROR_MAP = {
  "auth/user-not-found":         "auth.errors.userNotFound",
  "auth/wrong-password":         "auth.errors.wrongPassword",
  "auth/invalid-credential":     "auth.errors.invalidCredential",
  "auth/email-already-in-use":   "auth.errors.emailInUse",
  "auth/invalid-email":          "auth.errors.invalidEmail",
  "auth/weak-password":          "auth.errors.weakPassword",
  "auth/too-many-requests":      "auth.errors.tooManyRequests",
  "auth/network-request-failed": "auth.errors.networkFailed",
  "auth/popup-closed-by-user":   "auth.errors.popupClosed",
  "__timeout__":                 "auth.errors.timeout",
  "__name_taken__":              "auth.errors.nameTaken",
  "__email_not_verified__":      "auth.errors.emailNotVerified",
};

function getModalMsg(err, t) {
  const key = FIREBASE_ERROR_MAP[err?.code];
  if (key) return t(key);
  if (err?.message) return err.message;
  return t("auth.errors.generic");
}

function withTimeout(p, ms = 10000) {
  return Promise.race([p, new Promise((_, rej) => setTimeout(() => rej({ code: "__timeout__" }), ms))]);
}

// ═══════════════════════════════════════════════════════════════════════════════
// KÜÇÜK UI YARDICILARI
// ═══════════════════════════════════════════════════════════════════════════════

function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Ember({ style }) {
  return <span style={{ position: "absolute", width: 2, height: 2, borderRadius: "50%", background: "#d4a853", boxShadow: "0 0 6px 2px rgba(212,168,83,0.6)", pointerEvents: "none", ...style }} />;
}

function CornerBrackets({ dim }) {
  const s = { position: "absolute", fill: "none", strokeWidth: 1 };
  return (
    <>
      <svg width={20} height={20} viewBox="0 0 20 20" style={{ ...s, top: 10, left: 10 }} stroke={dim ? "rgba(212,168,83,0.15)" : "rgba(212,168,83,0.5)"}><path d="M1 10 L1 1 L10 1"/></svg>
      <svg width={20} height={20} viewBox="0 0 20 20" style={{ ...s, bottom: 10, right: 10 }} stroke={dim ? "rgba(212,168,83,0.15)" : "rgba(212,168,83,0.5)"}><path d="M19 10 L19 19 L10 19"/></svg>
    </>
  );
}

function GoldButton({ onClick, href, children, primary = true }) {
  const [hov, setHov] = useState(false);
  const st = {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: "13px 30px", cursor: "pointer",
    borderRadius: 8,
    fontFamily: "'Courier Prime', monospace", fontSize: 11,
    letterSpacing: "0.28em", textTransform: "uppercase", textDecoration: "none",
    border: primary ? `1px solid rgba(212,168,83,${hov ? 0.75 : 0.45})` : `1px solid rgba(212,168,83,${hov ? 0.6 : 0.4})`,
    background: primary ? `rgba(212,168,83,${hov ? 0.14 : 0.07})` : "transparent",
    color: primary ? (hov ? "var(--bone-text-primary)" : "var(--bone-text-accent)") : (hov ? "var(--bone-text-primary)" : "var(--bone-text-secondary)"),
    boxShadow: primary && hov ? "0 0 28px rgba(212,168,83,0.1)" : "none",
    transition: "all 0.35s ease",
  };
  const arrow = <span style={{ display: "inline-block", transform: hov ? "translateX(5px)" : "translateX(0)", transition: "transform 0.3s ease" }}>→</span>;
  if (onClick) return <button onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} onClick={onClick} style={st}>{children}{arrow}</button>;
  return <a href={href} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={st}>{children}{arrow}</a>;
}

function SectionHeading({ eyebrow, title }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{ marginBottom: 48, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}>
      <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: "0.35em", color: "var(--bone-text-accent)", textTransform: "uppercase", marginBottom: 14 }}>✦ {eyebrow} ✦</div>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(32px,5vw,52px)", fontWeight: 900, color: "var(--bone-text-primary)", lineHeight: 1.1, margin: 0 }}>{title}</h2>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODÜL KARTI
// ═══════════════════════════════════════════════════════════════════════════════
function ModuleCard({ mod, index, user, onAuthRequired }) {
  const { t }          = useTranslation();
  const [ref, visible] = useReveal();
  const [hov, setHov]  = useState(false);
  const navigate       = useNavigate();

  function handleClick(e) {
    e.preventDefault();
    if (!user) { onAuthRequired(); } else { navigate(mod.href); }
  }

  return (
    <a ref={ref} href={mod.href} onClick={handleClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        position: "relative", display: "block", textDecoration: "none",
        padding: "36px 32px 32px",
        borderRadius: 8,
        background: hov ? "linear-gradient(150deg,#1e160c 0%,#231a0d 100%)" : "linear-gradient(150deg,#17110a 0%,#1b1409 100%)",
        border: `1px solid rgba(212,168,83,${hov ? 0.6 : 0.4})`,
        boxShadow: hov ? "0 0 36px rgba(212,168,83,0.07)" : "none",
        transition: "all 0.4s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transitionDelay: `${index * 0.14}s`,
        transitionDuration: visible ? "0.8s" : "0s",
        cursor: "pointer",
      }}>
      <CornerBrackets dim={!hov} />
      <svg viewBox="0 0 24 24" style={{ width: 26, height: 26, marginBottom: 20, fill: `rgba(212,168,83,${hov ? 0.85 : 0.55})`, filter: hov ? "drop-shadow(0 0 6px rgba(212,168,83,0.35))" : "none", transition: "fill 0.3s ease, filter 0.3s ease" }}>
        <path d={mod.iconPath} />
      </svg>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(20px,2.4vw,26px)", fontWeight: 700, color: hov ? "var(--bone-text-primary)" : "var(--bone-text-secondary)", lineHeight: 1.15, marginBottom: 8, transition: "color 0.3s ease" }}>
        {t(mod.titleKey, mod.title)}
      </h3>
      <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: "0.2em", color: "var(--bone-text-accent)", textTransform: "uppercase", marginBottom: 18 }}>
        {t(mod.subtitleKey, mod.subtitle)}
      </div>
      <div style={{ height: 1, marginBottom: 18, background: `linear-gradient(90deg,transparent,rgba(212,168,83,${hov ? 0.35 : 0.1}),transparent)`, transition: "background 0.4s ease" }} />
      <p style={{ fontFamily: "'Courier Prime', monospace", fontSize: 13, lineHeight: 1.85, marginBottom: 28, color: hov ? "var(--bone-text-primary)" : "var(--bone-text-secondary)", transition: "color 0.3s ease" }}>
        {t(mod.descKey, mod.desc)}
      </p>
      <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: hov ? "var(--bone-text-accent)" : "var(--bone-text-muted)", display: "flex", alignItems: "center", gap: 10, transition: "color 0.3s ease" }}>
        {!user && <span style={{ fontSize: 12 }}>🔒</span>}
        <span>{user ? t("home.modules.enter", "Gir") : t("home.modules.loginRequired", "Giriş Gerekli")}</span>
        <span style={{ transform: hov ? "translateX(6px)" : "translateX(0)", transition: "transform 0.3s ease" }}>→</span>
      </div>
    </a>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// GÖZ İKONU + MODAL ŞİFRE GİRİŞİ
// ═══════════════════════════════════════════════════════════════════════════════
function EyeIcon({ open }) {
  if (open) return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,83,0.55)" strokeWidth="2" strokeLinecap="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(212,168,83,0.55)" strokeWidth="2" strokeLinecap="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function ModalPasswordInput({ value, onChange, onKeyDown, placeholder, inpStyle, show, onToggle }) {
  return (
    <div style={{ position: "relative", marginBottom: 12 }}>
      <input style={{ ...inpStyle, margin: 0, paddingRight: 40 }} type={show ? "text" : "password"} placeholder={placeholder} value={value} onChange={onChange} onKeyDown={onKeyDown} />
      <button type="button" onClick={onToggle} tabIndex={-1} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", borderRadius: 6, cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
        <EyeIcon open={show} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH MODAL — değiştirilmedi, birebir korundu
// ═══════════════════════════════════════════════════════════════════════════════
function AuthModal({ isOpen, onClose }) {
  const { login, register, loginWithGoogle } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode]               = useState("login");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPw, setConfirmPw]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed]           = useState(false);
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [loading, setLoading]         = useState(false);

  if (!isOpen) return null;

  const reset = () => { setEmail(""); setPassword(""); setConfirmPw(""); setDisplayName(""); setAgreed(false); setShowPassword(false); setError(""); setSuccess(""); };
  const switchMode = (m) => { setMode(m); reset(); };

  const inpStyle = {
    display: "block", width: "100%", boxSizing: "border-box",
    padding: "10px 13px", marginBottom: 12, borderRadius: 6,
    background: "#1a1208", border: "1px solid rgba(212,168,83,0.4)",
    color: "var(--bone-text-primary)", fontFamily: "'Courier Prime',monospace",
    fontSize: 12, outline: "none", letterSpacing: "0.04em",
  };
  const btnStyle = (variant = "primary") => ({
    width: "100%", padding: "12px", marginBottom: 10, borderRadius: 8,
    fontFamily: "'Courier Prime',monospace", fontSize: 11, letterSpacing: "0.22em",
    textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer",
    background: variant === "primary" ? "#d4a853" : "transparent",
    border: variant === "primary" ? "none" : "1px solid rgba(212,168,83,0.3)",
    color: variant === "primary" ? "#0d0a05" : "rgba(212,168,83,0.65)",
    opacity: loading ? 0.65 : 1, transition: "all 0.2s",
  });

  async function handleSubmit() {
    setError(""); setSuccess("");
    if (mode === "register") {
      if (!agreed)              { setError(t("auth.errors.termsRequired")); return; }
      if (!displayName.trim())  { setError(t("auth.errors.nameRequired")); return; }
      if (!email)               { setError(t("auth.errors.emailRequired")); return; }
      if (!password)            { setError(t("auth.errors.passwordRequired")); return; }
      if (password.length < 6)  { setError(t("auth.errors.passwordTooShort")); return; }
      if (password !== confirmPw) { setError(t("auth.errors.passwordMismatch")); return; }
      setLoading(true);
      try {
        const ok = await withTimeout(isDisplayNameAvailable(displayName.trim()));
        if (!ok) { setError(t("auth.errors.nameTaken")); setLoading(false); return; }
      } catch (e) { setError(getModalMsg(e, t)); setLoading(false); return; }
    } else {
      if (!email || !password) { setError(t("auth.errors.emailAndPwRequired")); return; }
      setLoading(true);
    }
    try {
      if (mode === "register") await withTimeout(register({ email, password, displayName: displayName.trim() }), 15000);
      else                     await withTimeout(login({ email, password }));
      onClose();
    } catch (e) {
      if (e?.code === "__verify_email__") {
        setSuccess(e.message || t("auth.verifyEmail"));
        setMode("login");
        setPassword(""); setConfirmPw(""); setDisplayName(""); setAgreed(false); setShowPassword(false);
      } else { setError(getModalMsg(e, t)); }
    } finally { setLoading(false); }
  }

  async function handleGoogle() {
    setError(""); setSuccess("");
    if (mode === "register" && !agreed) { setError(t("auth.errors.termsRequired")); return; }
    setLoading(true);
    try { await loginWithGoogle(); onClose(); }
    catch (e) { setError(getModalMsg(e, t)); }
    finally { setLoading(false); }
  }

  async function handleReset() {
    if (!email) { setError(t("auth.errors.emailRequired2")); return; }
    setLoading(true);
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      const { auth } = await import("../firebase/config");
      await sendPasswordResetEmail(auth, email);
      setSuccess(t("auth.verifyEmail"));
    } catch (e) { setError(getModalMsg(e, t)); }
    finally { setLoading(false); }
  }

  const modeLabel = {
    login:    t("auth.login"),
    register: t("auth.createAccount"),
    reset:    t("auth.resetPassword"),
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#100d07", border: "1px solid rgba(212,168,83,0.2)", borderRadius: 8, padding: "36px 32px", width: "100%", maxWidth: 380, maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 32, height: 32, borderRadius: 8, background: "none", border: "1px solid rgba(212,168,83,0.3)", color: "rgba(212,168,83,0.7)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, fontStyle: "italic", color: "#d4a853", marginBottom: 6, letterSpacing: "0.04em" }}>BoneCast</div>
        <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, letterSpacing: "0.28em", color: "var(--bone-text-muted)", textTransform: "uppercase", marginBottom: 24 }}>
          {modeLabel[mode]}
        </div>

        {mode === "register" && (
          <input style={inpStyle} type="text" placeholder={t("auth.displayName")} value={displayName} onChange={e => setDisplayName(e.target.value)} />
        )}
        {mode !== "reset" ? (
          <>
            <input style={inpStyle} type="email" placeholder={t("auth.email")} value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
            <ModalPasswordInput value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} placeholder={t("auth.password")} inpStyle={inpStyle} show={showPassword} onToggle={() => setShowPassword(s => !s)} />
            {mode === "register" && (
              <>
                <ModalPasswordInput value={confirmPw} onChange={e => setConfirmPw(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} placeholder={t("auth.passwordRepeat")} inpStyle={inpStyle} show={showPassword} onToggle={() => setShowPassword(s => !s)} />
                {/* Canlı şifre eşleşme göstergesi */}
                {confirmPw.length > 0 && (
                  <p style={{ fontSize: 10, marginTop: -8, marginBottom: 10, textAlign: "right", fontFamily: "'Courier Prime',monospace", color: password === confirmPw ? "#6ec97a" : "#e05c5c" }}>
                    {password === confirmPw ? t("auth.passwordMatch") : t("auth.passwordNoMatch")}
                  </p>
                )}
              </>
            )}
          </>
        ) : (
          <input style={inpStyle} type="email" placeholder={t("auth.email")} value={email} onChange={e => setEmail(e.target.value)} />
        )}

        {mode === "register" && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 2, accentColor: "#d4a853" }} />
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "var(--bone-text-secondary)", lineHeight: 1.7 }}>
                {t("auth.termsAgree")}{" "}
                <a href="/terms" target="_blank" style={{ color: "#d4a853", textDecoration: "none" }} rel="noreferrer">{t("auth.termsLink")}</a>
                {" "}{t("auth.termsAnd")}{" "}
                <a href="/privacy" target="_blank" style={{ color: "#d4a853", textDecoration: "none" }} rel="noreferrer">{t("auth.privacyLink")}</a>
                {"."}
              </span>
            </label>
          </div>
        )}

        {error   && <p style={{ color: "#e05c5c", fontFamily: "'Courier Prime',monospace", fontSize: 11, marginBottom: 12, textAlign: "center" }}>⚠ {error}</p>}
        {success && <p style={{ color: "#6ec97a", fontFamily: "'Courier Prime',monospace", fontSize: 11, marginBottom: 12, textAlign: "center" }}>✓ {success}</p>}

        {mode === "reset" ? (
          <button style={btnStyle()} onClick={handleReset} disabled={loading}>
            {loading ? "..." : t("auth.resetSend")}
          </button>
        ) : (
          <button style={btnStyle()} onClick={handleSubmit} disabled={loading}>
            {loading ? "..." : mode === "login" ? t("auth.login") : t("auth.register")}
          </button>
        )}

        {mode !== "reset" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: "rgba(212,168,83,0.1)" }} />
              <span style={{ color: "rgba(212,168,83,0.3)", fontSize: 10, fontFamily: "'Courier Prime',monospace" }}>{t("common.or")}</span>
              <div style={{ flex: 1, height: 1, background: "rgba(212,168,83,0.1)" }} />
            </div>
            <button style={btnStyle("outline")} onClick={handleGoogle} disabled={loading}>
              {t("auth.googlePrefix")} {mode === "login" ? t("auth.loginWith") : t("auth.registerWith")}
            </button>
          </>
        )}

        <div style={{ textAlign: "center", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: "var(--bone-text-secondary)", marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
          {mode === "login" && (
            <>
              <span>{t("auth.noAccount")}{" "}<span onClick={() => switchMode("register")} style={{ color: "#d4a853", cursor: "pointer", textDecoration: "underline" }}>{t("auth.register")}</span></span>
              <span><span onClick={() => switchMode("reset")} style={{ color: "rgba(212,168,83,0.5)", cursor: "pointer", textDecoration: "underline" }}>{t("auth.forgotPassword")}</span></span>
            </>
          )}
          {mode === "register" && <span>{t("auth.alreadyHave")}{" "}<span onClick={() => switchMode("login")} style={{ color: "#d4a853", cursor: "pointer", textDecoration: "underline" }}>{t("auth.login")}</span></span>}
          {mode === "reset"    && <span><span onClick={() => switchMode("login")} style={{ color: "#d4a853", cursor: "pointer", textDecoration: "underline" }}>{t("auth.backToLogin")}</span></span>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DONATE CTA STRIP
// ═══════════════════════════════════════════════════════════════════════════════
function DonateCTAStrip({ PX }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ref, visible] = useReveal(0.08);
  const [hov, setHov] = useState(false);

  return (
    <section ref={ref} style={{ padding: `0 ${PX} 80px` }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        background: "#100d07",
        borderRadius: 8,
        border: "1px solid rgba(212,168,83,0.4)",
        padding: "40px 48px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 32, flexWrap: "wrap",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
        position: "relative", overflow: "hidden",
      }}>
        {/* Arka plan parıltısı */}
        <div style={{
          position: "absolute", top: 0, right: 0, width: 300, height: "100%",
          background: "radial-gradient(ellipse at right,rgba(212,168,83,0.05) 0%,transparent 70%)",
          pointerEvents: "none",
        }} />
        <div style={{ flex: 1, minWidth: 260 }}>
          <p style={{
            fontFamily: "'Courier Prime',monospace", fontSize: 9,
            letterSpacing: "0.32em", color: "var(--bone-text-muted)",
            textTransform: "uppercase", marginBottom: 8,
          }}>
            ✦ {t('donate.homeStrip.eyebrow')} ✦
          </p>
          <h3 style={{
            fontFamily: "'Playfair Display',serif", fontSize: "clamp(18px,2.5vw,24px)",
            fontWeight: 700, color: "var(--bone-text-accent)", margin: "0 0 8px",
          }}>
            {t('donate.homeStrip.title')}
          </h3>
          <p style={{
            fontFamily: "'Courier Prime',monospace", fontSize: 11,
            color: "var(--bone-text-secondary)", lineHeight: 1.7,
            margin: 0, maxWidth: 480,
          }}>
            {t('donate.homeStrip.body')}
          </p>
        </div>
        <button
          onClick={() => navigate("/donate")}
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{
            padding: "12px 32px", flexShrink: 0, borderRadius: 8,
            background: "transparent",
            border: `1px solid ${hov ? "rgba(212,168,83,0.65)" : "rgba(212,168,83,0.4)"}`,
            color: "var(--bone-text-accent)",
            fontFamily: "'Courier Prime',monospace", fontWeight: "bold",
            fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.25s ease",
            boxShadow: hov ? "0 0 20px rgba(212,168,83,0.12)" : "none",
          }}
        >
          ♥ {t('donate.homeStrip.btn')}
        </button>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ÖZELLİKLER ŞERİDİ
// ═══════════════════════════════════════════════════════════════════════════════
function FeaturesStrip({ PX }) {
  const { t } = useTranslation();
  const [ref, visible] = useReveal(0.06);

  const FEATURES = [
    { icon: "♫", value: "16+", key: "platforms" },
    { icon: "◎", value: "2",   key: "languages"  },
    { icon: "✦", value: "3",   key: "modules"    },
    { icon: "♥", value: "0",   key: "ads"        },
    { icon: "⚡", value: "∞",  key: "lists"      },
  ];

  return (
    <section ref={ref} style={{ padding: `0 ${PX} 0`, marginBottom: 0 }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        display: "flex", flexWrap: "wrap",
        borderTop: "1px solid rgba(212,168,83,0.28)",
        borderBottom: "1px solid rgba(212,168,83,0.28)",
      }}>
        {FEATURES.map((f, i) => (
          <div key={f.key} style={{
            flex: "1 1 160px", padding: "28px 20px",
            borderRight: i < FEATURES.length - 1 ? "1px solid rgba(212,168,83,0.28)" : "none",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(16px)",
            transition: `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`,
          }}>
            <span style={{ fontSize: 18, color: "var(--bone-text-accent)" }}>{f.icon}</span>
            <span style={{
              fontFamily: "'Playfair Display',serif", fontSize: 30, fontWeight: 900,
              color: "var(--bone-text-accent)", lineHeight: 1,
            }}>{f.value}</span>
            <span style={{
              fontFamily: "'Courier Prime',monospace", fontSize: 8,
              letterSpacing: "0.22em", textTransform: "uppercase",
              color: "var(--bone-text-secondary)", textAlign: "center",
            }}>{t(`home.features.${f.key}`)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DILLER BÖLÜMü
// ═══════════════════════════════════════════════════════════════════════════════
function LanguagesSection({ PX }) {
  const { t } = useTranslation();
  const [ref, visible] = useReveal(0.08);

  const LANGS_ACTIVE = [
    { code: "TR", name: "Türkçe",  flag: "🇹🇷", note: t('home.langs.native') },
    { code: "EN", name: "English", flag: "🇬🇧", note: t('home.langs.native') },
  ];
  const LANGS_SOON = [
    { code: "DE", flag: "🇩🇪" },
    { code: "FR", flag: "🇫🇷" },
    { code: "ES", flag: "🇪🇸" },
    { code: "JA", flag: "🇯🇵" },
    { code: "RU", flag: "🇷🇺" },
    { code: "AR", flag: "🇸🇦" },
  ];

  return (
    <section ref={ref} style={{ padding: `80px ${PX}` }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionHeading
          eyebrow={t('home.langs.eyebrow')}
          title={t('home.langs.title')}
        />

        {/* Aktif diller */}
        <div style={{
          display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 40,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.7s ease, transform 0.7s ease",
        }}>
          {LANGS_ACTIVE.map((lang) => (
            <div key={lang.code} style={{
              flex: "1 1 200px",
              background: "#12100a",
              border: "1px solid rgba(212,168,83,0.2)",
              borderTop: "2px solid rgba(212,168,83,0.5)",
              padding: "24px 28px",
              display: "flex", alignItems: "center", gap: 16,
            }}>
              <span style={{ fontSize: 28 }}>{lang.flag}</span>
              <div>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "#d4a853", marginBottom: 4 }}>
                  {lang.name}
                </div>
                <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.2em", color: "var(--bone-text-muted)", textTransform: "uppercase" }}>
                  {lang.code} · {lang.note}
                </div>
              </div>
              <span style={{ marginLeft: "auto", fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(110,201,122,0.7)", letterSpacing: "0.2em" }}>✓ {t('home.langs.active')}</span>
            </div>
          ))}
        </div>

        {/* Yakında gelecek diller */}
        <div style={{
          opacity: visible ? 1 : 0,
          transition: "opacity 0.7s ease 0.2s",
        }}>
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.28em", color: "var(--bone-text-muted)", textTransform: "uppercase", marginBottom: 14 }}>
            ✦ {t('home.langs.comingSoon')} ✦
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {LANGS_SOON.map((lang) => (
              <div key={lang.code} style={{
                padding: "10px 18px",
                background: "rgba(212,168,83,0.03)",
                border: "1px solid rgba(212,168,83,0.35)",
                borderRadius: 6,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ fontSize: 16 }}>{lang.flag}</span>
                <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "var(--bone-text-muted)", letterSpacing: "0.12em" }}>{lang.code}</span>
              </div>
            ))}
            <div style={{
              padding: "10px 18px",
              background: "rgba(212,168,83,0.03)",
              border: "1px dashed rgba(212,168,83,0.4)",
              borderRadius: 6,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "var(--bone-text-muted)", letterSpacing: "0.12em" }}>+ {t('home.langs.more')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// YOL HARİTASI — ROADMAP
// ═══════════════════════════════════════════════════════════════════════════════
function RoadmapSection({ PX }) {
  const { t } = useTranslation();
  const [ref, visible] = useReveal(0.06);

  const ITEMS = [
    { phase: "I",   status: "done",    key: "bonebeat"  },
    { phase: "II",  status: "done",    key: "boneframe" },
    { phase: "III", status: "done",    key: "boneforge" },
    { phase: "IV",  status: "active",  key: "bonechat"  },
    { phase: "V",   status: "soon",    key: "bonebook"  },
    { phase: "VI",  status: "future",  key: "more"      },
  ];

  const STATUS_COLORS = {
    done:   { dot: "#6ec97a", label: "rgba(110,201,122,0.6)"  },
    active: { dot: "#d4a853", label: "rgba(212,168,83,0.8)"   },
    soon:   { dot: "var(--bone-text-muted)", label: "var(--bone-text-muted)" },
    future: { dot: "var(--bone-text-muted)", label: "var(--bone-text-muted)" },
  };

  return (
    <section ref={ref} style={{ padding: `0 ${PX} 80px` }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionHeading
          eyebrow={t('home.roadmap.eyebrow')}
          title={t('home.roadmap.title')}
        />

        <div style={{ position: "relative" }}>
          {/* Dikey zaman çizgisi */}
          <div style={{
            position: "absolute", left: 19, top: 8, bottom: 8, width: 1,
            background: "linear-gradient(to bottom, rgba(212,168,83,0.3), rgba(212,168,83,0.05))",
          }} />

          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {ITEMS.map((item, i) => {
              const colors = STATUS_COLORS[item.status];
              const isDone   = item.status === "done";
              const isActive = item.status === "active";
              return (
                <div key={item.key} style={{
                  display: "flex", alignItems: "flex-start", gap: 20,
                  padding: "16px 0",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateX(0)" : "translateX(-16px)",
                  transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
                }}>
                  {/* Dot */}
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: colors.dot, flexShrink: 0, marginTop: 5,
                    boxShadow: isActive ? `0 0 12px ${colors.dot}` : "none",
                    zIndex: 1,
                  }} />

                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{
                        fontFamily: "'Playfair Display',serif", fontStyle: "italic",
                        fontSize: 11, color: "var(--bone-text-muted)",
                      }}>{item.phase}</span>
                      <span style={{
                        fontFamily: "'Courier Prime',monospace", fontSize: 13,
                        color: isActive ? "var(--bone-text-primary)" : isDone ? "var(--bone-text-secondary)" : "var(--bone-text-muted)",
                        letterSpacing: "0.06em",
                        fontWeight: isActive ? "bold" : "normal",
                      }}>
                        {t(`home.roadmap.${item.key}.name`)}
                      </span>
                      {isActive && (
                        <span style={{
                          fontFamily: "'Courier Prime',monospace", fontSize: 8,
                          letterSpacing: "0.22em", textTransform: "uppercase",
                          color: colors.label,
                          border: `1px solid ${colors.dot}`,
                          padding: "2px 7px",
                          animation: "goldPulse 2s ease-in-out infinite",
                        }}>
                          {t('home.roadmap.inProgress')}
                        </span>
                      )}
                      {isDone && (
                        <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 8, color: "rgba(110,201,122,0.85)", letterSpacing: "0.18em" }}>
                          ✓ {t('home.roadmap.live')}
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontFamily: "'Courier Prime',monospace", fontSize: 10,
                      color: "var(--bone-text-secondary)",
                      margin: "4px 0 0", letterSpacing: "0.04em", lineHeight: 1.6,
                    }}>
                      {t(`home.roadmap.${item.key}.desc`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANİFESTO
// ═══════════════════════════════════════════════════════════════════════════════
function ManifestoSection({ PX }) {
  const [ref, visible] = useReveal(0.08);
  const { t } = useTranslation();
  const lines = [
    { text: t('home.manifesto.line1'), outline: false },
    { text: t('home.manifesto.line2'), outline: true  },
    { text: t('home.manifesto.line3'), outline: false },
    { text: t('home.manifesto.line4'), outline: false },
  ];
  return (
    <section ref={ref} style={{ padding: `100px ${PX} 90px`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", userSelect: "none", opacity: 0.016 }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(72px,17vw,250px)", fontWeight: 900, color: "#d4a853", letterSpacing: "-0.03em", whiteSpace: "nowrap" }}>BONECAST</span>
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 58, opacity: visible ? 1 : 0, transition: "opacity 0.6s ease" }}>
          <div style={{ flex: 1, height: 1, background: "rgba(212,168,83,0.09)" }} /><span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.35em", color: "var(--bone-text-accent)", textTransform: "uppercase" }}>✦ {t('home.manifesto.eyebrow')} ✦</span><div style={{ flex: 1, height: 1, background: "rgba(212,168,83,0.09)" }} />
        </div>
        <div style={{ marginBottom: 52 }}>
          {lines.map((line, i) => (
            <div key={i} style={{ overflow: "hidden" }}>
              <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(42px,7.5vw,105px)", fontWeight: 900, lineHeight: 1.0, letterSpacing: "-0.02em", color: line.outline ? "transparent" : "var(--bone-text-primary)", WebkitTextStroke: line.outline ? "1.5px rgba(212,168,83,0.6)" : "none", filter: line.outline ? "drop-shadow(0 0 12px rgba(212,168,83,0.15))" : "none", margin: 0, paddingBottom: 2, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(100%)", transition: `opacity 0.85s ease ${i * 0.12}s, transform 0.95s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s` }}>
                {line.text}
              </p>
            </div>
          ))}
        </div>
        <div style={{ maxWidth: 500, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "opacity 0.7s ease 0.55s, transform 0.7s ease 0.55s" }}>
          <p style={{ fontFamily: "'IM Fell English',serif", fontStyle: "italic", fontSize: "clamp(15px,1.75vw,18px)", lineHeight: 1.95, color: "var(--bone-text-secondary)", margin: 0 }}>
            {t('home.manifesto.body')}
          </p>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANA BİLEŞEN
// ═══════════════════════════════════════════════════════════════════════════════
export default function Home() {
  const { t, i18n }     = useTranslation();
  const [authOpen, setAuthOpen] = useState(false);
  const { user }        = useAuth();
  const { isAdmin }     = useAdmin();
  const canOpenAdmin    = isAdmin || MODERATOR_UIDS.includes(user?.uid);
  const { openSidebar } = useSidebar();
  const [mounted, setMounted] = useState(false);
  const location        = useLocation();
  const PX              = "clamp(24px,8vw,120px)";

  useEffect(() => {
    if (!document.getElementById("bc-fonts")) {
      const link = document.createElement("link");
      link.id = "bc-fonts"; link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Courier+Prime:ital,wght@0,400;0,700;1,400&family=IM+Fell+English:ital@0;1&display=swap";
      document.head.appendChild(link);
    }
    const timer = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (location.state?.openAuth && !user) {
      setAuthOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user]);

  return (
    <>
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />

      <style>{`
        @keyframes goldPulse {
          0%,100% { text-shadow: 0 0 40px rgba(212,168,83,0.22),0 0 80px rgba(212,168,83,0.08),2px 2px 0 rgba(0,0,0,0.85); }
          50%      { text-shadow: 0 0 65px rgba(212,168,83,0.38),0 0 130px rgba(212,168,83,0.15),2px 2px 0 rgba(0,0,0,0.85); }
        }
        @keyframes emberFloat {
          0%   { transform:translateY(0) scale(1);opacity:0; }
          8%   { opacity:1; }
          88%  { opacity:0.7; }
          100% { transform:translateY(-130px) translateX(18px) scale(0.2);opacity:0; }
        }
        @keyframes scrollDrop {
          0%,100% { transform:translateY(0);opacity:0.5; }
          50%     { transform:translateY(8px);opacity:1; }
        }
      `}</style>

      {/* Grain overlay */}
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: 5, pointerEvents: "none", opacity: 0.052, backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: "180px 180px" }} />
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, zIndex: 4, pointerEvents: "none", background: "radial-gradient(ellipse at center,transparent 38%,rgba(7,5,1,0.5) 72%,rgba(4,3,0,0.88) 100%)" }} />

      <div style={{ position: "relative", zIndex: 10, minHeight: "100vh", background: "var(--bone-dark)" }}>

        {/* ── HEADER ── */}
        <header style={{ position: "fixed", top: 0, left: 0, right: 0, padding: "18px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 9999, background: "rgba(10,8,4,0.96)", backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(212,168,83,0.3)" }}>
          <img src={logo} alt="BoneCast Logo" onClick={openSidebar} title={t("header.openMenu", "Menüyü Aç")}
            style={{ width: 40, height: 40, borderRadius: "50%", border: "1px solid rgba(212,168,83,0.3)", cursor: "pointer", transition: "box-shadow 0.25s ease, border-color 0.25s ease" }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 16px rgba(212,168,83,0.3)"; e.currentTarget.style.borderColor = "rgba(212,168,83,0.6)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "rgba(212,168,83,0.3)"; }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!user && (
              <div role="group" aria-label={t("header.language", "Language")} style={{ display: "flex", border: "1px solid rgba(212,168,83,0.4)", borderRadius: 8, overflow: "hidden" }}>
                {["tr", "en"].map(language => {
                  const activeLanguage = (i18n.resolvedLanguage || i18n.language).slice(0, 2).toLowerCase();
                  const active = activeLanguage === language;
                  return (
                    <button
                      key={language}
                      type="button"
                      onClick={() => i18n.changeLanguage(language)}
                      aria-pressed={active}
                      style={{
                        padding: "7px 9px", border: "none",
                        borderRight: language === "tr" ? "1px solid rgba(212,168,83,0.4)" : "none",
                        background: active ? "rgba(212,168,83,0.18)" : "transparent",
                        color: active ? "var(--bone-text-primary)" : "var(--bone-text-muted)",
                        fontFamily: "'Courier Prime',monospace", fontSize: 10,
                        letterSpacing: "0.08em", cursor: "pointer",
                      }}
                    >{language.toUpperCase()}</button>
                  );
                })}
              </div>
            )}
            {!user ? (
              <button onClick={() => setAuthOpen(true)} style={{ background: "transparent", border: "1px solid #d4a853", borderRadius: 8, color: "#d4a853", padding: "8px 20px", fontFamily: "'Courier Prime',monospace", fontSize: 12, cursor: "pointer", letterSpacing: "2px" }}>
                {t("header.login", "HESAP GİRİŞ")}
              </button>
            ) : (
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: "var(--bone-text-accent)", letterSpacing: "0.12em", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                ✦ {user.displayName || user.email}
              </span>
            )}
          </div>
        </header>

        {/* ── HERO ── */}
        <section style={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: `140px ${PX} 90px`, overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 80% 55% at 50% 105%,rgba(28,16,3,0.95) 0%,transparent 65%),radial-gradient(ellipse 55% 40% at 18% 50%,rgba(212,168,83,0.04) 0%,transparent 60%),radial-gradient(ellipse 45% 45% at 82% 18%,rgba(212,168,83,0.03) 0%,transparent 60%)" }} />
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.022, backgroundImage: "repeating-linear-gradient(0deg,#d4a853 0px,#d4a853 1px,transparent 1px,transparent 64px)" }} />

          {mounted && [
            { left: "14%", top: "72%", delay: "0s",   dur: "7s"   },
            { left: "24%", top: "76%", delay: "2.2s", dur: "9s"   },
            { left: "68%", top: "68%", delay: "0.9s", dur: "7.5s" },
            { left: "79%", top: "74%", delay: "3.5s", dur: "8.5s" },
            { left: "46%", top: "81%", delay: "1.8s", dur: "6.5s" },
          ].map((e, i) => (
            <Ember key={i} style={{ left: e.left, top: e.top, animation: `emberFloat ${e.dur} ease-in ${e.delay} infinite` }} />
          ))}

          <div style={{ position: "relative", maxWidth: 1200, margin: "0 auto", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 52, opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(-18px)", transition: "opacity 0.8s ease 0.1s,transform 0.8s ease 0.1s" }}>
              <div style={{ width: 44, height: 1, background: "rgba(212,168,83,0.38)" }} />
              <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, letterSpacing: "0.35em", color: "var(--bone-text-accent)", textTransform: "uppercase" }}>
                {t("hero.tagline", "Est. 2026 · Beta")}
              </span>
              <div style={{ width: 44, height: 1, background: "rgba(212,168,83,0.38)" }} />
            </div>

            <div style={{ overflow: "hidden" }}>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontStyle: "italic", fontSize: "clamp(76px,15.5vw,210px)", lineHeight: 0.9, letterSpacing: "-0.02em", color: "var(--bone-text-primary)", margin: 0, display: "block", opacity: mounted ? 1 : 0, transform: mounted ? "translateX(0)" : "translateX(-70px)", transition: "opacity 1s ease 0.2s,transform 1s cubic-bezier(0.16,1,0.3,1) 0.2s", animation: mounted ? "goldPulse 7s ease-in-out infinite 1.8s" : "none" }}>BONE</h1>
            </div>
            <div style={{ overflow: "hidden", marginTop: -6 }}>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontStyle: "italic", fontSize: "clamp(76px,15.5vw,210px)", lineHeight: 0.9, letterSpacing: "-0.02em", color: "transparent", WebkitTextStroke: "1.5px rgba(212,168,83,0.55)", filter: "drop-shadow(0 0 18px rgba(212,168,83,0.18))", margin: 0, display: "block", paddingLeft: "clamp(14px,2.5vw,42px)", opacity: mounted ? 1 : 0, transform: mounted ? "translateX(0)" : "translateX(70px)", transition: "opacity 1s ease 0.38s,transform 1s cubic-bezier(0.16,1,0.3,1) 0.38s" }}>CAST</h1>
            </div>

            <div style={{ marginTop: 52, display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 32, opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(28px)", transition: "opacity 0.8s ease 0.65s,transform 0.8s ease 0.65s" }}>
              <div style={{ maxWidth: 400 }}>
                <p style={{ fontFamily: "'IM Fell English',serif", fontStyle: "italic", fontSize: "clamp(16px,1.9vw,20px)", color: "var(--bone-text-secondary)", lineHeight: 1.75, margin: "0 0 10px" }}>
                  {t("hero.quote", '"Keşfedilmeyi bekleyen bir ses arşivi. Müziği; derle, konuş, oyna."')}
                </p>
                <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.22em", color: "var(--bone-text-muted)", textTransform: "uppercase", margin: 0 }}>
                  {t("hero.quoteAuthor", "— BoneCast Manifestosu")}
                </p>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {/* ── CTA butonları — rotalar güncellendi ── */}
                {user
                  ? <GoldButton href="/bonebeat" primary>{t("hero.cta.enter", "Arşive Gir")}</GoldButton>
                  : <GoldButton onClick={() => setAuthOpen(true)} primary>{t("hero.cta.enter", "Arşive Gir")}</GoldButton>
                }
                {user
                  ? <GoldButton href="/boneframe" primary={false}>{t("hero.cta.community", "Topluluğa Katıl")}</GoldButton>
                  : <GoldButton onClick={() => setAuthOpen(true)} primary={false}>{t("hero.cta.community", "Topluluğa Katıl")}</GoldButton>
                }
              </div>
            </div>

            <div style={{ marginTop: 60, paddingTop: 28, borderTop: "1px solid rgba(212,168,83,0.09)", display: "flex", gap: 48, flexWrap: "wrap", opacity: mounted ? 1 : 0, transition: "opacity 0.8s ease 0.95s" }}>
              {[
                { val: "∞",    label: t("hero.stats.playlists", "Müzik Listesi") },
                { val: "III",  label: t("hero.stats.modules", "Aktif Modül") },
                { val: "Beta", label: t("hero.stats.phase", "Aşama") },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: "var(--bone-text-accent)", letterSpacing: "0.04em" }}>{s.val}</div>
                  <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.24em", color: "var(--bone-text-secondary)", textTransform: "uppercase", marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, opacity: mounted ? 0.8 : 0, transition: "opacity 1s ease 1.5s" }}>
            <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, letterSpacing: "0.32em", color: "rgba(212,168,83,0.7)", textTransform: "uppercase" }}>{t("hero.scrollHint", "Aşağı")}</span>
            <div style={{ width: 1, height: 36, background: "linear-gradient(to bottom,rgba(212,168,83,0.5),transparent)", animation: "scrollDrop 2.2s ease-in-out infinite" }} />
          </div>
        </section>

        {/* ── Ayırıcı ── */}
        <div style={{ margin: `0 ${PX}`, height: 1, background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.18),transparent)" }} />

        {/* ── MODÜLLER ── */}
        <section style={{ padding: `80px ${PX} 100px` }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <SectionHeading
              eyebrow={t("home.modules.eyebrow", "Platform")}
              title={t("home.modules.title", "Modüller")}
            />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))", gap: 18 }}>
              {MODULES.map((mod, i) => (
                <ModuleCard key={mod.id} mod={mod} index={i} user={user} onAuthRequired={() => setAuthOpen(true)} />
              ))}
            </div>

            {/* Admin Paneli linki */}
            {canOpenAdmin && (
              <div style={{ marginTop: 24 }}>
                <a href="/admin" style={{ display: "flex", alignItems: "center", gap: 18, padding: "20px 28px", textDecoration: "none", background: "linear-gradient(135deg,rgba(224,92,92,0.06) 0%,rgba(180,40,40,0.04) 100%)", border: "1px solid rgba(224,92,92,0.22)", transition: "all 0.3s ease" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(224,92,92,0.5)"; e.currentTarget.style.background = "linear-gradient(135deg,rgba(224,92,92,0.1) 0%,rgba(180,40,40,0.07) 100%)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(224,92,92,0.22)"; e.currentTarget.style.background = "linear-gradient(135deg,rgba(224,92,92,0.06) 0%,rgba(180,40,40,0.04) 100%)"; }}>
                  <svg viewBox="0 0 24 24" fill="rgba(224,92,92,0.7)" width={22} height={22}><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5z" /></svg>
                  <div>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 700, color: "rgba(224,92,92,0.85)", letterSpacing: "0.04em" }}>{t("home.admin.title", "Admin Paneli")}</div>
                    <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 9, color: "rgba(224,92,92,0.4)", letterSpacing: "0.22em", textTransform: "uppercase", marginTop: 3 }}>{t("home.admin.sub", "Yönetim & Kullanıcı Denetimi")}</div>
                  </div>
                  <span style={{ marginLeft: "auto", fontFamily: "'Courier Prime',monospace", fontSize: 11, color: "rgba(224,92,92,0.4)" }}>→</span>
                </a>
              </div>
            )}
          </div>
        </section>

        {/* ── Ayırıcı ── */}
        <div style={{ margin: `0 ${PX}`, height: 1, background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.18),transparent)" }} />

        {/* ── ÖZELLİKLER ŞERİDİ ── */}
        <FeaturesStrip PX={PX} />

        {/* ── AYIRICI ── */}
        <div style={{ margin: `0 ${PX}`, height: 1, background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.1),transparent)" }} />

        {/* ── DİLLER ── */}
        <LanguagesSection PX={PX} />

        {/* ── AYIRICI ── */}
        <div style={{ margin: `0 ${PX}`, height: 1, background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.1),transparent)" }} />

        {/* ── YOL HARİTASI ── */}
        <RoadmapSection PX={PX} />

        {/* ── AYIRICI ── */}
        <div style={{ margin: `0 ${PX}`, height: 1, background: "linear-gradient(90deg,transparent,rgba(212,168,83,0.18),transparent)" }} />

        <ManifestoSection PX={PX} />
        <DonateCTAStrip PX={PX} />
        <Footer />
      </div>
    </>
  );
}
